import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "@/store";
import type { Dataset, DatasetFilter, WeightValues } from "@/types/dataset";

// Memoized base selectors
const selectDatasetState = (state: RootState) => state.dataset;

export const selectDatasets = createSelector(
  [selectDatasetState],
  (datasetState) => datasetState.datasets
);

export const selectFilters = createSelector(
  [selectDatasetState],
  (datasetState) => datasetState.filters
);

export const selectWeightConfigBaseline = createSelector(
  [selectDatasetState],
  (datasetState) => datasetState.weightConfigBaseline
);

export const selectWeightValues = createSelector(
  [selectDatasetState],
  (datasetState): WeightValues => datasetState.weightValues
);

const selectWeightedDatasetIds = createSelector(
  [selectWeightValues],
  (weightValues) => Object.keys(weightValues)
);

export const selectIsWeightConfigSaved = createSelector(
  [selectWeightConfigBaseline],
  (baseline) => baseline !== null
);

export const selectWeightAdjustedDatasetIds = createSelector(
  [selectWeightConfigBaseline, selectWeightedDatasetIds],
  (baseline, weightedDatasetIds) => {
    if (baseline === null) return [];

    const baselineIds = new Set(baseline);
    return weightedDatasetIds.filter((id) => !baselineIds.has(id));
  }
);

export const selectIsWeightAutoAdjusted = createSelector(
  [
    selectWeightConfigBaseline,
    selectWeightedDatasetIds,
    selectWeightAdjustedDatasetIds
  ],
  (baseline, weightedDatasetIds, adjustedDatasetIds) => {
    if (baseline === null) return false;
    if (adjustedDatasetIds.length > 0) return true;

    const weightedIds = new Set(weightedDatasetIds);
    return baseline.some((id) => !weightedIds.has(id));
  }
);

// Memoized dataset-specific selectors
export const selectDatasetById = (datasetId: string) =>
  createSelector(
    [selectDatasets],
    (datasets): Dataset | undefined => datasets[datasetId]
  );

// Memoized selector factory
const createDatasetFiltersSelector = (() => {
  const cache = new Map();
  return (datasetId: string) => {
    if (!cache.has(datasetId)) {
      cache.set(
        datasetId,
        createSelector(
          [selectFilters],
          (filters): DatasetFilter[] => filters[datasetId] || []
        )
      );
    }
    return cache.get(datasetId);
  };
})();

export const selectDatasetFilters = createDatasetFiltersSelector;
